import React, { useState, useEffect } from 'react';
import { FaTrash, FaPlus, FaDownload, FaUser, FaUserTag, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import Swal from 'sweetalert2';
import DataTable from '../../components_shared/DataTable';
import { userRolesAPI, userAPI, roleAPI } from '../../utils/api';
import { useApp } from '../../context/AppContext';

export default function UserRolesTable() {
  const { permissions, roles: userRoles } = useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUserRole, setSelectedUserRole] = useState(null);
  const [form, setForm] = useState({ userId: '', roleIds: [] });
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const isSuperAdmin = userRoles && (userRoles.includes('SUPER_ADMIN') || userRoles.includes('ROLE_SUPER_ADMIN'));

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await userAPI.getUsers({ page: 0, size: 1000 });
      setUsers(res.data.content || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await roleAPI.getRoles({ page: 0, size: 1000 });
      const rolesData = Array.isArray(res.data) ? res.data : (res.data.content || []);
      setRoles(rolesData);
    } catch (err) {
      console.error('Failed to fetch roles:', err);
    }
  };

  const fetchUserRoles = async (userId) => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await userRolesAPI.getUserRoles(userId);
      const userData = res.data;
      if (userData && userData.roles) {
        const userRolesData = userData.roles.map(role => ({
          id: `${userData.id}-${role.id}`,
          userId: userData.id,
          username: userData.username,
          email: userData.email,
          roleId: role.id,
          roleName: role.name,
          roleDescription: role.description
        }));
        setData(userRolesData);
        setSelectedUser(userData);
      } else {
        setData([]);
        setSelectedUser(userData);
      }
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch user roles');
      setData([]);
      setSelectedUser(null);
    } finally {
      setLoading(false);
    }
  };

  const openAssign = () => {
    setForm({ userId: '', roleIds: [] });
    setSelectedUserRole(null);
    setShowAssignModal(true);
  };

  const closeModal = () => {
    setShowAssignModal(false);
    setShowDeleteModal(false);
    setSelectedUserRole(null);
    setForm({ userId: '', roleIds: [] });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'userId') {
      setForm({ ...form, [name]: value });
      if (value) {
        fetchUserRoles(value);
      } else {
        setData([]);
        setSelectedUser(null);
      }
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleRoleChange = (roleId) => {
    setForm(prev => ({
      ...prev,
      roleIds: prev.roleIds.includes(roleId)
        ? prev.roleIds.filter(id => id !== roleId)
        : [...prev.roleIds, roleId]
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.userId || form.roleIds.length === 0) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please select a user and at least one role.',
        icon: 'error',
        confirmButtonColor: '#f44336',
      });
      return;
    }
    setLoading(true);
    try {
      await userRolesAPI.assignRoles(form.userId, form.roleIds);
      Swal.fire({
        title: 'Success',
        text: 'Roles assigned successfully!',
        icon: 'success',
        confirmButtonColor: '#4CAF50',
      });
      closeModal();
      fetchUserRoles(form.userId);
    } catch (error) {
      let msg = error.message;
      if (error.response && error.response.data && error.response.data.message) {
        msg = error.response.data.message;
      }
      Swal.fire({
        title: 'Error',
        text: msg || 'Failed to assign roles. Please try again.',
        icon: 'error',
        confirmButtonColor: '#f44336',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUserRole) return;
    setLoading(true);
    try {
      await userRolesAPI.removeRoles(selectedUserRole.userId, [selectedUserRole.roleId]);
      Swal.fire('Success', 'Role removed successfully', 'success');
      closeModal();
      fetchUserRoles(selectedUserRole.userId);
    } catch (err) {
      Swal.fire('Error', err.message || 'Failed to remove role', 'error');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['User ID', 'Username', 'Email', 'Role ID', 'Role Name', 'Role Description'];
    const rows = data.map(ur => [ur.userId, ur.username, ur.email, ur.roleId, ur.roleName, ur.roleDescription]);
    const csvContent = `data:text/csv;charset=utf-8,${[headers.join(','), ...rows.map(e => e.join(','))].join('\n')}`;
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'user_roles.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getRoleBadge = (roleName) => {
    if (!roleName) return null;
    let color = '#6c757d';
    if (roleName === 'SUPER_ADMIN') color = '#10ac84';
    else if (roleName === 'ADMIN') color = '#007bff';
    else if (roleName === 'USER') color = '#fbc02d';
    return (
      <span style={{
        background: color,
        color: '#fff',
        borderRadius: '12px',
        padding: '4px 12px',
        fontSize: '0.85em',
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}>{roleName.replace('_', ' ')}</span>
    );
  };

  const columns = [
    {
      accessorKey: 'userId',
      header: 'User ID',
      size: 80,
    },
    {
      accessorKey: 'username',
      header: 'Username',
      size: 150,
    },
    {
      accessorKey: 'email',
      header: 'Email',
      size: 200,
    },
    {
      accessorKey: 'roleName',
      header: 'Role',
      size: 150,
      cell: ({ getValue }) => getRoleBadge(getValue()),
    },
    {
      accessorKey: 'roleDescription',
      header: 'Role Description',
      size: 200,
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 100,
      cell: ({ row }) => (
        <div className="eduss-global-action-buttons">
          {(permissions.includes('EDIT_USERS') || isSuperAdmin) && (
            <button
              onClick={() => { setSelectedUserRole(row.original); setShowDeleteModal(true); }}
              className="eduss-global-action-btn delete"
              title="Remove Role"
              aria-label={`Remove role ${row.original.roleName} from user ${row.original.username}`}
              disabled={loading}
            >
              <FaTrash />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="eduss-user-table">
      <div className="eduss-global-table-header">
        <div className="eduss-global-table-title">
          <h2>User Roles Management</h2>
          <p>Assign and manage roles for users in the system</p>
        </div>
        <div className="eduss-global-table-actions">
          {(permissions.includes('EDIT_USERS') || isSuperAdmin) && (
            <button
              className="eduss-global-table-btn add"
              onClick={openAssign}
              disabled={loading}
            >
              <FaPlus /> Assign Roles
            </button>
          )}
          <button
            className="eduss-global-table-btn export"
            onClick={exportToCSV}
            disabled={loading || data.length === 0}
          >
            <FaDownload /> Export Data
          </button>
        </div>
      </div>

      {/* User Selection */}
      <div className="eduss-user-roles-selection">
        <div className="eduss-user-roles-select-wrapper">
          <FaUser className="eduss-user-roles-select-icon" />
          <select
            value={form.userId}
            onChange={handleChange}
            name="userId"
            className="eduss-user-roles-select"
            disabled={loading}
          >
            <option value="">Select a user to view their roles</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>
                {user.username} ({user.email})
              </option>
            ))}
          </select>
        </div>
        {selectedUser && (
          <div className="eduss-user-roles-info">
            <div className="eduss-user-roles-avatar">
              <img src="/default-user-icon.png" alt={selectedUser.username} />
            </div>
            <div className="eduss-user-roles-details">
              <h4>{selectedUser.username}</h4>
              <p>{selectedUser.email}</p>
              <div className="eduss-user-roles-status">
                Status: <span className={`status-badge ${selectedUser.status?.toLowerCase()}`}>
                  {selectedUser.status}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <span>Loading user roles...</span>
        </div>
      ) : error ? (
        <div style={{ textAlign: 'center', color: '#e74c3c', padding: 40 }}>{error}</div>
      ) : !form.userId ? (
        <div style={{ textAlign: 'center', color: '#888', padding: 40 }}>
          Please select a user to view their assigned roles.
        </div>
      ) : data.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#888', padding: 40 }}>
          No roles assigned to this user.
        </div>
      ) : (
        <DataTable
          data={data}
          columns={columns}
          permissions={permissions}
        />
      )}

      {/* Assign Roles Modal */}
      {showAssignModal && (
        <div className="eduss-global-modal-overlay" onClick={closeModal}>
          <div className="eduss-user-create-modal" onClick={e => e.stopPropagation()}>
            <div className="eduss-user-create-header">
              <div className="eduss-user-create-header-content">
                <div className="eduss-user-create-icon">
                  <FaUserTag />
                </div>
                <div className="eduss-user-create-title">
                  <h3>Assign Roles to User</h3>
                  <p>Select a user and the roles you want to assign</p>
                </div>
              </div>
              <button className="eduss-user-create-close-btn" onClick={closeModal}>
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit} className="eduss-user-create-form">
              <div className="eduss-user-create-sections">
                {/* User Selection Section */}
                <div className="eduss-user-create-section">
                  <div className="eduss-user-create-section-header">
                    <FaUser className="eduss-user-create-section-icon" />
                    <h4>Select User</h4>
                  </div>
                  <div className="eduss-user-create-field-grid">
                    <div className="eduss-user-create-field">
                      <label htmlFor="assignUserId">
                        Choose User <span className="eduss-user-create-required">*</span>
                      </label>
                      <div className="eduss-user-create-input-wrapper">
                        <FaUser className="eduss-user-create-input-icon" />
                        <select
                          id="assignUserId"
                          name="userId"
                          value={form.userId}
                          onChange={handleChange}
                          className="eduss-user-create-select"
                          required
                          disabled={loading}
                        >
                          <option value="">Select a user</option>
                          {users.map(user => (
                            <option key={user.id} value={user.id}>
                              {user.username} ({user.email})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Role Selection Section */}
                <div className="eduss-user-create-section">
                  <div className="eduss-user-create-section-header">
                    <FaUserTag className="eduss-user-create-section-icon" />
                    <h4>Select Roles to Assign</h4>
                  </div>
                  <div className="eduss-user-create-field-grid">
                    {roles.map(role => (
                      <div key={role.id} className="eduss-user-create-field">
                        <label className="eduss-user-create-role-label">
                          <input
                            type="checkbox"
                            checked={form.roleIds.includes(role.id)}
                            onChange={() => handleRoleChange(role.id)}
                            disabled={loading}
                            className="eduss-user-create-role-checkbox"
                          />
                          <div className="eduss-user-create-role-content">
                            <div className="eduss-user-create-role-name">
                              {getRoleBadge(role.name)}
                            </div>
                            <div className="eduss-user-create-role-description">
                              {role.description || 'No description available'}
                            </div>
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>
                  {roles.length === 0 && (
                    <div className="eduss-user-create-no-roles">
                      <p>No roles available. Please create roles first.</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="eduss-user-create-actions">
                <button
                  type="button"
                  onClick={closeModal}
                  className="eduss-user-create-btn cancel"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="eduss-user-create-btn submit"
                  disabled={loading || !form.userId || form.roleIds.length === 0}
                >
                  {loading ? (
                    <>
                      <div className="eduss-user-create-btn-spinner"></div>
                      Assigning...
                    </>
                  ) : (
                    <>
                      <FaCheckCircle className="eduss-user-create-btn-icon" />
                      Assign {form.roleIds.length > 0 ? `${form.roleIds.length} Role${form.roleIds.length > 1 ? 's' : ''}` : 'Roles'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedUserRole && (
        <div className="eduss-global-modal-overlay" onClick={closeModal}>
          <div className="eduss-global-modal" onClick={e => e.stopPropagation()}>
            <div className="eduss-global-modal-header">
              <h3>Remove Role from User</h3>
              <button onClick={closeModal}>&times;</button>
            </div>
            <div className="eduss-global-modal-content">
              <p>
                Are you sure you want to remove role <strong>{getRoleBadge(selectedUserRole.roleName)}</strong> from user <strong>{selectedUserRole.username}</strong>?
              </p>
              <p>This action cannot be undone.</p>
              <div className="eduss-global-modal-actions">
                <button
                  onClick={closeModal}
                  className="eduss-global-modal-btn cancel"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="eduss-global-modal-btn delete"
                  disabled={loading}
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}